import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Col, Container, Row } from "react-bootstrap";
import GetData from "../../services/getData";
import TitleWithIcon from "../../components/layout/pageTitle/PageTitle";
import kbIcon from "../../media/template_plan.png";
import BlockTitle from "../../components/layout/blockTitle/BlockTitle";
import TiptapEditor from "../../components/layout/tiptapEditor/TiptapEditor";
import TiptapViewer from "../../components/layout/tiptapViewer/TiptapViewer";

function htmlToPlainTrim(html) {
  if (!html || typeof html !== "string") return "";
  return html
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function KnowledgeBaseArticle() {
  const { t } = useTranslation("knowledge_base");
  const { id } = useParams();
  const navigate = useNavigate();
  const id_article = Number(id);

  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [citiesMap, setCitiesMap] = useState({});
  const [formData, setFormData] = useState({
    name: "",
    text: "",
    category_id: "",
  });
  const [saving, setSaving] = useState(false);

  const loadingArticle = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await GetData({
        module: "knowledge_base",
        block: "info",
        id_article,
      });

      let articleData = data?.result ?? data?.request?.result ?? {};
      if (Array.isArray(articleData) && articleData.length > 0) {
        articleData = articleData[0];
      }
      if (typeof articleData !== "object" || articleData === null) {
        articleData = {};
      }

      setCitiesMap(data?.cities_map ?? data?.request?.cities_map ?? {});
      if (!articleData || Object.keys(articleData).length === 0) {
        setError(t("article.notFound"));
        setArticle(null);
      } else {
        setArticle(articleData);
      }
    } catch (err) {
      console.error("Ошибка загрузки статьи:", err);
      setError(err.message || t("article.errors.loadFailed"));
      setArticle(null);
    } finally {
      setLoading(false);
    }
  }, [id_article, t]);

  useEffect(() => {
    if (!id_article || Number.isNaN(id_article)) {
      setLoading(false);
      setError(t("article.invalidId"));
      return;
    }
    loadingArticle();
  }, [id_article, loadingArticle, t]);

  useEffect(() => {
    setFormData({
      name: article?.name || "",
      text: article?.text || "",
      category_id:
        article?.category_id !== undefined && article?.category_id !== null
          ? String(article.category_id)
          : "",
    });
  }, [article]);

  useEffect(() => {
    const title = formData.name || article?.name;
    if (title) {
      document.title = title;
    } else {
      document.title = t("list.title");
    }
  }, [formData.name, article?.name, t]);

  const handleBack = () => {
    navigate("/knowledge_base");
  };

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    if (article) {
      setFormData({
        name: article.name || "",
        text: article.text || "",
        category_id:
          article.category_id !== undefined && article.category_id !== null
            ? String(article.category_id)
            : "",
      });
    }
  };

  const handleDeleteClick = async () => {
    if (!id_article) return;

    const confirmDelete = window.confirm(t("article.confirmDelete"));
    if (!confirmDelete) return;

    try {
      setLoading(true);
      await GetData({
        module: "knowledge_base",
        block: "info",
        id_article,
        action: "delete",
      });

      try {
        const checkData = await GetData({
          module: "knowledge_base",
          block: "info",
          id_article,
        });
        const still =
          checkData?.result &&
          typeof checkData.result === "object" &&
          Object.keys(checkData.result).length > 0;
        if (still) {
          alert(t("article.errors.deleteFailed"));
        } else {
          navigate("/knowledge_base");
        }
      } catch (checkErr) {
        console.error("Ошибка проверки удаления статьи:", checkErr);
        alert(t("article.errors.deleteCheckFailed"));
        navigate("/knowledge_base");
      }
    } catch (err) {
      console.error("Ошибка удаления статьи:", err);
      alert(t("article.errors.deleteFailed"));
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.name.trim() || !htmlToPlainTrim(formData.text)) {
      alert(t("article.errors.needTitleAndText"));
      return;
    }

    if (!formData.category_id) {
      alert(t("article.errors.needCategory"));
      return;
    }

    setSaving(true);
    try {
      await GetData({
        module: "knowledge_base",
        block: "info",
        id_article,
        action: "change",
        data: {
          name: formData.name.trim(),
          text: formData.text,
          category_id: Number(formData.category_id),
        },
      });

      await loadingArticle();
      setIsEditing(false);
    } catch (err) {
      console.error("Ошибка сохранения:", err);
      alert(t("article.errors.saveFailed"));
    } finally {
      setSaving(false);
    }
  };

  if (loading && !article) {
    return (
      <Container fluid="md" className="mt-4">
        {t("state.loading", { ns: "common" })}
      </Container>
    );
  }

  if (error) {
    return (
      <Container fluid="md" className="mt-4">
        <div className="text-danger mb-3">
          {t("error", { ns: "common", message: error })}
        </div>
        <button className="button-default light" onClick={handleBack}>
          {t("toList", { ns: "common" })}
        </button>
      </Container>
    );
  }

  const blockButtons = isEditing
    ? [
        {
          label: saving
            ? t("saving", { ns: "common" })
            : t("actions.save", { ns: "common" }),
          onClick: handleSave,
          type: "normal",
        },
        {
          label: t("actions.cancel", { ns: "common" }),
          onClick: handleCancelEdit,
          type: "light",
        },
        {
          label: t("actions.delete", { ns: "common" }),
          onClick: handleDeleteClick,
          type: "danger",
        },
      ]
    : [
        {
          label: (
            <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
              <svg
                viewBox="0 0 24 24"
                aria-hidden="true"
                width="20"
                height="20"
                style={{ fill: "currentColor" }}>
                <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zm2.92 2.33H5v-.92l9.06-9.06.92.92L5.92 19.58zM20.71 7.04a1.003 1.003 0 0 0 0-1.42L18.37 3.29a1.003 1.003 0 0 0-1.42 0l-1.83 1.83 3.75 3.75 1.84-1.83z" />
              </svg>
              {t("actions.edit", { ns: "common" })}
            </span>
          ),
          onClick: handleEditClick,
          type: "light icon",
        },
        {
          label: (
            <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
              <svg
                viewBox="0 0 24 24"
                aria-hidden="true"
                width="20"
                height="20"
                style={{ color: "currentColor" }}>
                <path
                  d="M9 14L4 9l5-5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M4 9h11a4 4 0 1 1 0 8H9"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              {t("toList", { ns: "common" })}
            </span>
          ),
          onClick: handleBack,
          type: "normal icon",
        },
      ];

  const pageTitle = formData.name || article?.name || t("untitled", { ns: "common" });

  const categoryLabel =
    article?.category_name ||
    (formData.category_id &&
      Object.keys(citiesMap).find(
        (name) => String(citiesMap[name]) === String(formData.category_id),
      )) ||
    "—";

  return (
    <Container fluid="md">
      <Row>
        <Col>
          <div className="patient-page">
            <TitleWithIcon
              icon={kbIcon}
              title={t("list.title")}
              showStatus={false}
              status={""}
            />
          </div>
        </Col>
      </Row>
      <Row>
        <Col>
          <div className="tab-content block page-block">
            <Row>
              <Col>
                <BlockTitle title={pageTitle} buttons={blockButtons} />
              </Col>
            </Row>

            <Row >
              <Col>
                <div className="page">
                  <div className="page-content">
                    {!isEditing ? (
                      <div className="patient-recommendations mt-4">
                        <div className="recommendation-item mb-4">
                          <h5>{formData.name || "—"}</h5>
                          <div className="my-3 text-muted small">
                            {t("article.category", { name: categoryLabel })}
                          </div>
                          <TiptapViewer
                            content={formData.text}
                            emptyText={t("article.emptyText")}
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="patient-recommendations mt-4">
                        <div className="mb-3">
                          <label className="mb-2">{t("article.titleLabel")}</label>
                          <input
                            type="text"
                            className="form-control"
                            value={formData.name}
                            onChange={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                name: e.target.value,
                              }))
                            }
                            placeholder={t("article.titlePlaceholder")}
                          />
                        </div>
                        <div className="mb-3">
                          <label className="mb-2">{t("article.categoryLabel")}</label>
                          <select
                            className="form-control mb-2"
                            value={formData.category_id}
                            onChange={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                category_id: e.target.value,
                              }))
                            }
                          >
                            <option value="">{t("modal.selectCategory")}</option>
                            {Object.entries(citiesMap).map(([catName, catId]) => (
                              <option key={catId} value={catId}>
                                {catName}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="mb-3">
                          <label className="mb-2">{t("article.articleLabel")}</label>
                          <TiptapEditor
                            key={id_article}
                            value={formData.text}
                            onChange={(html) =>
                              setFormData((prev) => ({ ...prev, text: html }))
                            }
                            placeholder={t("article.articlePlaceholder")}
                            minHeight="200px"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </Col>
            </Row>
          </div>
        </Col>
      </Row>
    </Container>
  );
}

export default KnowledgeBaseArticle;
